import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { UserPlus, MoreVertical, KeyRound, UserCheck, UserX, Trash2, Crown, ShieldCheck, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

const ROLE_META = {
  super_admin: { label: "Super Admin", icon: Crown, color: "bg-amber-50 text-amber-700 border-amber-200" },
  landlord:    { label: "Landlord",    icon: ShieldCheck, color: "bg-blue-50 text-blue-700 border-blue-200" },
  employee:    { label: "Employee",    icon: UserIcon, color: "bg-slate-100 text-slate-700 border-slate-200" },
};

const BLANK = { email: "", password: "", first_name: "", last_name: "", phone: "", role: "employee" };

export default function TeamPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const [pwdTarget, setPwdTarget] = useState(null);
  const [pwdValue, setPwdValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const isSuper = user?.role === "super_admin";

  const load = async () => {
    const { data } = await api.get("/employees");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("First name and last name are required (used to build the username)");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/employees", form);
      toast.success(`Account created · @${data.username}`);
      setOpen(false); setForm(BLANK);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Create failed");
    }
    setSaving(false);
  };

  const setActive = async (m, active) => {
    try {
      await api.patch(`/employees/${m.id}`, { is_active: active });
      toast.success(`${m.name} ${active ? "activated" : "deactivated"}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed"); }
  };

  const changePassword = async () => {
    if (!pwdValue || pwdValue.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post(`/employees/${pwdTarget.id}/password`, { password: pwdValue });
      toast.success(`Password updated for ${pwdTarget.name}`);
      setPwdTarget(null); setPwdValue("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed"); }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/employees/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  const previewUsername = (form.first_name && form.last_name) ? `${form.first_name}.${form.last_name}` : null;

  return (
    <AppLayout
      title="Team / Employees"
      subtitle="Manage Super Admin, Landlord and Employee accounts"
      actions={
        <Button data-testid="new-employee-button" onClick={() => { setForm(BLANK); setOpen(true); }} className="bg-ink text-ivory hover:bg-gold hover:text-ink rounded-none h-11 px-5 uppercase tracking-[0.2em] text-xs font-semibold transition-colors duration-300 gap-2">
          <UserPlus className="h-4 w-4" /> New account
        </Button>
      }
    >
      <Card className="rounded-none border border-bone overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-ivoryDark">
              <TableHead className="text-ink">Name</TableHead>
              <TableHead className="text-ink">Username</TableHead>
              <TableHead className="text-ink">Email</TableHead>
              <TableHead className="text-ink">Role</TableHead>
              <TableHead className="text-ink">Status</TableHead>
              <TableHead className="text-ink text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="team-list">
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10 italic">No team members yet</TableCell></TableRow>
            ) : items.map((m) => {
              const meta = ROLE_META[m.role] || ROLE_META.employee;
              const Icon = meta.icon;
              const active = m.is_active !== false;
              const isSelf = m.id === user?.id || m.email === user?.email;
              const isSuperRow = m.role === "super_admin";
              return (
                <TableRow key={m.id} data-testid={`employee-row-${m.id}`} className="hover:bg-ivory">
                  <TableCell>
                    <div className="font-medium">{m.name}</div>
                    {isSelf && <div className="text-[10px] uppercase tracking-widest text-gold">— You</div>}
                  </TableCell>
                  <TableCell><span className="font-mono-num text-sm text-slate-700">@{m.username}</span></TableCell>
                  <TableCell className="text-sm text-slate-600">{m.email}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 border ${meta.color}`}>
                      <Icon className="h-3 w-3" /> {meta.label}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span data-testid={`employee-status-${m.id}`} className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 border ${active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-rose-500"}`} />
                      {active ? "Active" : "Inactive"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`employee-actions-${m.id}`} disabled={isSelf}>
                          <MoreVertical className="h-4 w-4 text-slate-500" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-500">Admin actions</DropdownMenuLabel>
                        {!isSuperRow && (active ? (
                          <DropdownMenuItem data-testid={`deactivate-employee-${m.id}`} onClick={() => setActive(m, false)} className="gap-2 cursor-pointer">
                            <UserX className="h-4 w-4 text-amber-600" /> Deactivate
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem data-testid={`activate-employee-${m.id}`} onClick={() => setActive(m, true)} className="gap-2 cursor-pointer">
                            <UserCheck className="h-4 w-4 text-emerald-600" /> Activate
                          </DropdownMenuItem>
                        ))}
                        {(!isSuperRow || isSuper) && (
                          <DropdownMenuItem data-testid={`change-employee-password-${m.id}`} onClick={() => { setPwdTarget(m); setPwdValue(""); }} className="gap-2 cursor-pointer">
                            <KeyRound className="h-4 w-4 text-blue-600" /> Change password…
                          </DropdownMenuItem>
                        )}
                        {!isSuperRow && <DropdownMenuSeparator />}
                        {!isSuperRow && (
                          <DropdownMenuItem data-testid={`delete-employee-${m.id}`} onClick={() => setDeleteTarget(m)} className="gap-2 cursor-pointer text-rose-600 focus:text-rose-700">
                            <Trash2 className="h-4 w-4" /> Delete…
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle>New team account</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">First name *</Label>
                <Input data-testid="employee-first-name" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Last name *</Label>
                <Input data-testid="employee-last-name" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </div>
            </div>
            {previewUsername && (
              <div className="text-xs px-3 py-2 bg-gold/10 border border-gold/30 text-ink">
                <span className="micro-label text-gold mr-2">Username</span>
                <span className="font-mono-num">@{previewUsername}</span>
                <span className="text-slate-500 ml-2">(auto-generated, used for audit trail)</span>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Email *</Label>
              <Input data-testid="employee-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Password * (min 6)</Label>
                <Input data-testid="employee-password" type="text" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="employee-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee — add-only access</SelectItem>
                  {isSuper && <SelectItem value="landlord">Landlord — full access</SelectItem>}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">{form.role === "employee" ? "Employees can add records but cannot edit, delete, or run admin actions." : "Landlords have full access to all data and admin actions (creating other landlords requires Super Admin)."}</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="save-employee-button" type="submit" disabled={saving} className="bg-ink text-ivory hover:bg-gold hover:text-ink rounded-none uppercase tracking-[0.2em] text-xs font-semibold">
                {saving ? "Saving…" : "Create account"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change password dialog */}
      <Dialog open={!!pwdTarget} onOpenChange={(o) => { if (!o) { setPwdTarget(null); setPwdValue(""); } }}>
        <DialogContent data-testid="employee-password-dialog">
          <DialogHeader><DialogTitle>Change password</DialogTitle></DialogHeader>
          {pwdTarget && (
            <div className="space-y-4 pt-2">
              <div className="bg-ivory border border-bone px-3 py-2 text-sm">
                <div className="font-medium">{pwdTarget.name}</div>
                <div className="text-xs text-slate-500">@{pwdTarget.username} · {pwdTarget.email}</div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">New password</Label>
                <Input data-testid="employee-new-password" type="text" autoComplete="new-password" minLength={6} value={pwdValue} onChange={(e) => setPwdValue(e.target.value)} placeholder="At least 6 characters" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPwdTarget(null); setPwdValue(""); }}>Cancel</Button>
            <Button data-testid="save-employee-password" onClick={changePassword} className="bg-ink text-ivory hover:bg-gold hover:text-ink rounded-none uppercase tracking-[0.2em] text-xs font-semibold gap-1.5">
              <KeyRound className="h-3.5 w-3.5" /> Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-employee-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this account?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && <>This will permanently delete <strong>{deleteTarget.name}</strong> (@{deleteTarget.username}). This action cannot be undone.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-employee" onClick={remove} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
